import React, { useContext, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { UserDataContext } from 'context';
import TailwindLayout from 'TailwindcssComponents/TailwindLayout';
import TopBar from 'TailwindcssComponents/TopBar';
import FooterTailwind from 'TailwindcssComponents/Footer';

export default function UserRoleInvite() {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const ENVIRONMENT =
        apiUrl === 'https://dev.seatsnaps.com/'
            ? 'development'
            : apiUrl === 'https://production.seatsnaps.com/'
                ? 'production'
                : 'unknown';
    const bearerToken = localStorage.getItem('token');
    const { userData, selectedOrganization } = useContext(UserDataContext);
    const userRole = userData?.isAdmin ? 'admin' : selectedOrganization?.name || 'user';
    const orgId = selectedOrganization?.organization?.id || null;
    const navigate = useNavigate();
    const [permissions, setPermissions] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [selectedPermissions, setSelectedPermissions] = useState([]);
    const [defaultRoles, setDefaultRoles] = useState([]);
    const [selectedRole, setSelectedRole] = useState([]);
    const [selectedRoleInviteType, setSelectedRoleInviteType] = useState('Custom Role');

    if (!!orgId && userRole === 'admin') {
        setSelectedRoleInviteType('Custom Role');
    }

    const handlePermissionChange = (permissionId) => {
        setSelectedPermissions((prevSelected) => {
            const updatedPermissions = prevSelected.includes(permissionId)
                ? prevSelected.filter((id) => id !== permissionId)
                : [...prevSelected, permissionId];
            newInvitation.setFieldValue("permissions", updatedPermissions);
            return updatedPermissions;
        });
    };

    const handleRoleChange = (role) => {
        setSelectedRole(role);
        newInvitation.setFieldValue(
            "permissions",
            role?.defaultPermissions?.map(p => p.id) || []
        );
    };

    const fetchPermissions = async () => {
        try {
            const [permissionsResponse, permissionTypesResponse] = await Promise.all([
                axios.get(`${apiUrl}auth-service/permission`, { headers: { 'Authorization': `Bearer ${bearerToken}` } }),
                axios.get(`${apiUrl}auth-service/permission-type`, { headers: { 'Authorization': `Bearer ${bearerToken}` } }),
            ]);

            if (!permissionsResponse.data?.success || !permissionsResponse.data?.data) {
                throw new Error('Failed to fetch permissions');
            }
            if (!permissionTypesResponse.data?.success || !permissionTypesResponse.data?.data) {
                throw new Error('Failed to fetch permission types');
            }

            const permissions = permissionsResponse.data.data;
            const permissionTypes = permissionTypesResponse.data.data;

            // Initialize permission types with empty permission arrays
            const permissionTypeMap = permissionTypes.reduce((acc, type) => {
                acc[type.id] = { ...type, permissions: [] };
                return acc;
            }, {});

            // Group permissions under their respective permission types
            permissions.forEach(permission => {
                if (permission.permissionType && permissionTypeMap[permission.permissionType]) {
                    permissionTypeMap[permission.permissionType].permissions.push(permission);
                }
            });

            // Convert object back to array
            const mergedData = Object.values(permissionTypeMap);
            setPermissions(mergedData);
        } catch (error) {
            console.error(error.response?.data?.message || error.message);
        }
    };

    const fetchDefaultUserRoles = async () => {
        try {
            const response = await axios.get(`${apiUrl}auth-service/default-role`,
                {
                    headers: { 'Authorization': `Bearer ${bearerToken}` }
                });
            if (response.data?.success) {
                setDefaultRoles(response.data.data);
                console.log('roles', response.data.data);
            } else throw new Error(response.data?.message || 'Error in fetching user roles');
        } catch (error) {
            console.error(error.response?.data?.message || error.message);
        }
    };

    const fetchOrganizations = async () => {
        try {
            const response = await axios.get(`${apiUrl}auth-service/org?isArchived=false`,
                {
                    headers: { 'Authorization': `Bearer ${bearerToken}` }
                });
            if (response.data?.success) {
                setOrganizations(response.data.data);
            } else throw new Error(response.data?.message || 'Error in fetching organization');
        } catch (error) {
            console.error(error.response?.data?.message || error.message);
        }
    };

    useEffect(() => {
        // fetchUsers();
        fetchPermissions();
        fetchOrganizations();
        fetchDefaultUserRoles();
    }, [bearerToken]);

    useEffect(() => {
        newInvitation.setFieldValue(
            "permissions",
            selectedRoleInviteType === 'Custom Role'
                ? selectedPermissions
                : selectedRole?.defaultPermissions?.map(p => p.id) || []
        );
    }, [selectedPermissions, selectedRole, selectedRoleInviteType]);

    const onSubmit = async (values, { resetForm }) => {
        try {
            const payLoad = {
                ...values,
                orgId: values.orgId || orgId,
                roleName: values.roleName || selectedRole?.name,
                permissions: selectedPermissions.length > 0
                    ? selectedPermissions
                    : selectedRole?.defaultPermissions?.map(p => p.id) || [],
                redirectUrl: ENVIRONMENT === 'production'
                    ? `https://admin.seatsnaps.com/authentication/sign-in?name=${values.roleName}&orgName=${values.orgName}`
                    : `https://dev-admin.seatsnaps.com/authentication/sign-in?name=${values.roleName}&orgName=${values.orgName}`,
            };
            const response = await axios.post(`${apiUrl}auth-service/user-role/invite`, payLoad, {
                headers: { 'Authorization': `Bearer ${bearerToken}` },
            });
            if (response.data?.success) {
                toast.info(response.data.message || 'User invited successfully');
            } else {
                throw new Error("Failed to invite user");
            }
        } catch (error) {
            toast.error(error.response?.data?.message || error.message);
        } finally {
            resetForm();
            setTimeout(() => {
                navigate(-1);
            }, 1500);
        }
    };

    const newInvitation = useFormik({
        initialValues: {
            roleName: '',
            email: '',
            orgId: orgId || '',
            permissions: [],
            selectedRoleInviteType: selectedRoleInviteType,
        },
        validationSchema: Yup.object({
            selectedRoleInviteType: Yup.string(),
            roleName: Yup.string().when('selectedRoleInviteType', {
                is: 'Custom Role',
                then: (schema) => schema.required('Required'),
                otherwise: (schema) => schema.notRequired(),
            }),
            email: Yup.string().required('Required'),
            orgId: Yup.string().required('Required'),
            permissions: Yup.array()
                .min(1, 'At least one permission is required')
                .of(Yup.string().required()),
        }),
        onSubmit,
    });

    return (
        <TailwindLayout>
            <TopBar />
            <div className="p-12 min-h-screen mb-8">
                <div className="grid grid-cols-1 gap-6">
                    <div className="col-span-1">
                        <form onSubmit={newInvitation.handleSubmit}>
                            <div className="bg-white rounded-lg shadow-md">

                                {/* Form Content */}
                                <div className="p-4 space-y-4">
                                    <h2 className="text-xl font-semibold text-gray-900 border-b pb-4">User Role Invitation</h2>

                                    {/* role invitation type */}
                                    <div className='p-1'>
                                        <div className='relative w-full flex space-x-4'>
                                            {["Default Role", "Custom Role"].map((role) => (
                                                <div key={role} className="mb-4 border p-3 rounded-lg shadow-md">
                                                    <label className="flex items-center space-x-2">
                                                        <input
                                                            type="radio"
                                                            name="roleInviteType"
                                                            value={role}
                                                            checked={selectedRoleInviteType === role}
                                                            onChange={() => {
                                                                setSelectedRoleInviteType(role);
                                                                newInvitation.setFieldValue("selectedRoleInviteType", role);
                                                                setSelectedPermissions([]);
                                                                setSelectedRole([]);
                                                            }}
                                                            className="h-4 w-4"
                                                            disabled={!!orgId && userRole === 'admin'}
                                                        />
                                                        <h3 className="text-md font-semibold">{role}</h3>
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Role Name Field */}
                                    {selectedRoleInviteType === 'Custom Role' && (
                                        <div className="p-1">
                                            <div className="relative w-full">
                                                <label>
                                                    Role Name
                                                </label>
                                                <input
                                                    className={`w-full px-4 py-2 border ${newInvitation.touched.roleName && Boolean(newInvitation.errors.roleName)
                                                        ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                                                        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                                                        } rounded-md shadow-sm focus:outline-none focus:ring-2`}
                                                    id="roleName"
                                                    name="roleName"
                                                    type="text"
                                                    placeholder="e.g. Event Organizer"
                                                    value={newInvitation.values.roleName}
                                                    onChange={newInvitation.handleChange}
                                                    onBlur={newInvitation.handleBlur}
                                                />
                                                {newInvitation.touched.roleName && newInvitation.errors.roleName && (
                                                    <p className="mt-1 text-sm text-red-500">{newInvitation.errors.roleName}</p>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* User Field */}
                                    <div className="p-1">
                                        <div className="relative w-full">
                                            <label>
                                                Email of the user
                                            </label>
                                            <input
                                                className={`w-full px-4 py-2 border ${newInvitation.touched.email && Boolean(newInvitation.errors.email)
                                                    ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                                                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                                                    } rounded-md shadow-sm focus:outline-none focus:ring-2`}
                                                id="email"
                                                name="email"
                                                type="email"
                                                placeholder="e.g. avc@smail.com"
                                                value={newInvitation.values.email}
                                                onChange={newInvitation.handleChange}
                                                onBlur={newInvitation.handleBlur}
                                            />
                                            {newInvitation.touched.email && newInvitation.errors.email && (
                                                <p className="mt-1 text-sm text-red-500">{newInvitation.errors.email}</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Select Organization Field */}
                                    <div className="p-1">
                                        <div className="relative w-full">
                                            <label >
                                                Organization
                                            </label>
                                            <select
                                                className={`w-full px-3 py-2.5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-[45px] ${!!orgId ? 'bg-gray-100 cursor-not-allowed' : ''
                                                    }`}
                                                name="orgId"
                                                value={orgId || newInvitation.values.organizations}
                                                onChange={(e) => {
                                                    if (!orgId) {
                                                        const selectedOrgId = e.target.value;
                                                        const selectedOrg = organizations.find((org) => org.id === selectedOrgId);
                                                        newInvitation.setFieldValue('orgId', selectedOrgId);
                                                        newInvitation.setFieldValue('orgName', selectedOrg?.name || '');
                                                    }
                                                }}
                                                onBlur={newInvitation.handleBlur}
                                                disabled={!!orgId}
                                            >
                                                <option value="" disabled selected>Select Organization</option>
                                                {organizations?.map((org) => (
                                                    <option key={org.id} value={org.id}>
                                                        {org.name}
                                                    </option>
                                                ))}
                                            </select>
                                            {newInvitation.touched.orgId && newInvitation.errors.orgId && (
                                                <p className="mt-1 text-sm text-red-500">{newInvitation.errors.orgId}</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Select Permissions Field */}
                                    {selectedRoleInviteType === 'Custom Role' && (
                                        <div className="p-4">
                                            <h2 className="text font-bold mb-4">Select Permissions</h2>
                                            {permissions.map((type) => (
                                                <div key={type.id} className="mb-4 border p-3 rounded-lg shadow-md">
                                                    <h3 className="text font-semibold mb-2">{type.name}</h3>
                                                    {type.permissions.length > 0 ? (
                                                        <div className="grid grid-cols-4 gap-4">
                                                            {type.permissions.map((permission) => (
                                                                <label key={permission.id} className="flex items-center space-x-2">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedPermissions.includes(permission.id)}
                                                                        onChange={() => handlePermissionChange(permission.id)}
                                                                        className="h-4 w-4"
                                                                    />
                                                                    <span>{permission.name}</span>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <p className="text-gray-500">No permissions available</p>
                                                    )}

                                                </div>
                                            ))}
                                            <div className="mt-4">
                                                <h3 className="text font-semibold mb-4 mt-4">Selected Permissions:</h3>
                                                <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                                                    {selectedPermissions.map((id) => {
                                                        const permission = permissions
                                                            .flatMap((type) => type.permissions)
                                                            .find((p) => p.id === id);

                                                        return permission ? (
                                                            <li key={id} className="p-2 bg-gray-100 rounded-md text-center">
                                                                {permission.name}
                                                            </li>
                                                        ) : null;
                                                    })}
                                                </ul>
                                            </div>
                                            {newInvitation.touched.permissions && newInvitation.errors.permissions && (
                                                <p className="text-red-500 text-sm mt-2">{newInvitation.errors.permissions}</p>
                                            )}
                                        </div>
                                    )}

                                    {/* Select default roles */}
                                    {selectedRoleInviteType === 'Default Role' && (
                                        <div className="p-4">
                                            <h2 className="text font-bold mb-4">Select Default Roles</h2>
                                            {defaultRoles.map((role) => (
                                                <div key={role.id} className="mb-4 border p-3 rounded-lg shadow-md">
                                                    <label className="flex items-center space-x-2">
                                                        <input
                                                            type="radio"
                                                            name="defaultRole"
                                                            value={role.id}
                                                            checked={selectedRole?.id === role.id}
                                                            onChange={() => handleRoleChange(role)}
                                                            className="h-4 w-4"
                                                        />
                                                        <h3 className="text font-semibold">{role.name}</h3>
                                                    </label>
                                                    {role.defaultPermissions.length > 0 ? (
                                                        <ul className="mt-2 text-gray-700">
                                                            {role.defaultPermissions.map((permission) => (
                                                                <li key={permission.id} className="ml-6">- {permission.name}</li>
                                                            ))}
                                                        </ul>
                                                    ) : (
                                                        <p className="text-gray-500 ml-6">No permissions available</p>
                                                    )}
                                                </div>
                                            ))}
                                            {newInvitation.touched.permissions && newInvitation.errors.permissions && (
                                                <p className="text-red-500 text-sm mt-2">{newInvitation.errors.permissions}</p>
                                            )}
                                        </div>
                                    )}

                                    {/* Submit Button */}
                                    <div className="p-1">
                                        <button
                                            type="submit"
                                            className="px-4 py-2 bg-blue-600 text-white font-medium rounded-md shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                        >
                                            Invite
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            <FooterTailwind />
            <ToastContainer
                position="bottom-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </TailwindLayout>
    );
}